import React, { ReactNode } from "react";
import { PolicyCalloutFrame } from "./PolicyCalloutFrame";
import Tootbrush from "@/shared/components/PolicyCalloutTest/assets/toothbrush.svg";
import Bandage from "@/shared/components/PolicyCalloutTest/assets/bandage.svg";
import Stethoscope from "@/shared/components/PolicyCalloutTest/assets/stethoscope.svg";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import FullCoverageDetailsButton from "../FullCoverageDetailsButton";
import { useModal } from "@/shared/contexts/ModalProvider";
import FullCoverageDetailsContent from "../FullCoverageDetailsModal";
import Strings from "@/shared/utils/Strings.constants";
import { SAMPLE_POLICY_URL } from "@/quote-ptz-us/src/utils/constants";

interface CoverageItemProps {
    image: ReactNode;
    title: string;
    optional?: boolean;
    description: string;
    onOpenModal: () => void;
}

const CoverageItem: React.FC<CoverageItemProps> = ({ image, title, optional = false, description, onOpenModal }) => {
    return (
        <div className="flex items-center gap-3">
            <div className="flex size-12 items-center justify-center">{image}</div>
            <div className="flex flex-col gap-[2px] text-content-primary">
                <div className="flex items-center gap-2 text-base font-bold">
                    {title}
                    {optional && <Badge variant="dark">Optional</Badge>}
                </div>
                <div className="text-sm font-medium">
                    {description},{" "}
                    <Button variant="link" className="h-fit p-0 text-sm leading-4 text-content-brand-b underline" onClick={onOpenModal}>
                        much more.
                    </Button>
                </div>
            </div>
        </div>
    );
};

const VariantCoverage = () => {
    const { showModal } = useModal();

    const data = [
        {
            image: <Stethoscope className="size-12" />,
            title: "Illnesses",
            optional: false,
            description: "Skin allergy, cancer, ear infection, diarrhea, arthritis"
        },
        {
            image: <Bandage className="size-12" />,
            title: "Accidents",
            optional: false,
            description: "Surgery, hospitalization, broken bones, injuries"
        },
        {
            image: <Tootbrush className="size-12" />,
            title: "Wellness",
            optional: true,
            description: "Vaccinations, dental cleaning, spay & neuter"
        }
    ];

    const handleOpenModal = React.useCallback(() => {
        showModal(
            "coverage-details-modal",
            <FullCoverageDetailsContent showTelehealth samplePolicyUrl={SAMPLE_POLICY_URL} isDentalCovered isMicrochippingCovered showCompellingStats hasIllnessCoverage />,
            Strings.COVERAGE_DETAILS
        );
    }, [showModal]);

    return (
        <div className="flex flex-col items-start gap-4 lg:items-center lg:gap-6">
            <PolicyCalloutFrame
                title="Awarded by pet experts. Trusted by pet parents."
                tagline={undefined}
                showAccolades={true}
                accoladesVariant="coverage"
                accoladesPosition="above"
                showWhySpotBox={true}
                whySpotBadgeText="What's covered"
                titleClassName="text-center sm:text-start lg:text-2xl lg:text-center xl:text-[40px] xl:leading-[1.2] xl:font-extrabold"
                contentClassName="!p-4 !gap-4"
            >
                {data.map((item, index) => (
                    <CoverageItem key={index} image={item.image} title={item.title} optional={item.optional} description={item.description} onOpenModal={handleOpenModal} />
                ))}
            </PolicyCalloutFrame>
            <FullCoverageDetailsButton onClick={handleOpenModal} buttonClass="h-8" />
        </div>
    );
};

export default VariantCoverage;
